import { create } from 'zustand';

/**
 * Prediction screen state.
 *
 * Preview URLs are `URL.createObjectURL` handles and are revoked by
 * `clearImages` / `removeImage`, not left to the garbage collector: an object
 * URL pins the whole File in memory until it is revoked, and a user running
 * several folders of photos through this screen would otherwise accumulate all
 * of them for the life of the tab.
 */

/**
 * A frozen module constant, not a fresh `[]` per call.
 *
 * zustand v5 compares selector results by identity, so a selector ending in
 * `|| []` hands back a new array every time and re-renders forever. Same trap
 * as `selectActiveMessages` in the assistant store.
 */
const EMPTY = Object.freeze([]);

const revoke = (item) => {
  if (item?.previewUrl) {
    try {
      URL.revokeObjectURL(item.previewUrl);
    } catch {
      /* a browser that refuses to revoke is not a reason to lose the state update */
    }
  }
};

const usePredictionStore = create((set, get) => ({
  // ── the catalogue ─────────────────────────────────────────────────────────
  catalogue: null, // { models, total_versions, trained, runnable } — null until first load
  catalogueLoading: false,
  catalogueError: '',
  search: '',

  // ── what is selected ──────────────────────────────────────────────────────
  selectedVersionId: null,
  conf: 0.25,

  // ── the image queue ───────────────────────────────────────────────────────
  images: EMPTY,
  activeImageId: null,
  rejected: EMPTY,
  running: false,
  highlightedDetection: null,

  setCatalogue: (catalogue) => set({ catalogue, catalogueError: '' }),
  setCatalogueLoading: (catalogueLoading) => set({ catalogueLoading }),
  setCatalogueError: (catalogueError) => set({ catalogueError }),
  setSearch: (search) => set({ search }),
  setConf: (conf) => set({ conf }),
  setRunning: (running) => set({ running }),
  setHighlightedDetection: (highlightedDetection) => set({ highlightedDetection }),

  /**
   * Selecting a different version clears the RESULTS but keeps the IMAGES.
   *
   * "Run these same photos through the other model" is the single most likely
   * next action on this screen, and making the user re-attach them to do it
   * would be the screen throwing away the only thing it was given.
   */
  selectVersion: (selectedVersionId) =>
    set((state) => ({
      selectedVersionId,
      highlightedDetection: null,
      images: state.images.map((item) => ({ ...item, status: 'pending', result: null, error: '' })),
    })),

  addImages: (items, rejected = []) =>
    set((state) => {
      const images = state.images.concat(items);
      return {
        images,
        rejected,
        activeImageId: state.activeImageId || items[0]?.id || null,
      };
    }),

  setActiveImage: (activeImageId) => set({ activeImageId, highlightedDetection: null }),

  patchImage: (id, patch) =>
    set((state) => ({
      images: state.images.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    })),

  removeImage: (id) =>
    set((state) => {
      const target = state.images.find((item) => item.id === id);
      revoke(target);
      const images = state.images.filter((item) => item.id !== id);
      return {
        images: images.length ? images : EMPTY,
        activeImageId: state.activeImageId === id ? images[0]?.id || null : state.activeImageId,
      };
    }),

  clearImages: () => {
    get().images.forEach(revoke);
    set({ images: EMPTY, activeImageId: null, rejected: EMPTY, highlightedDetection: null });
  },

  /** Back to pending, results dropped — what the Run-again button does. */
  resetResults: () =>
    set((state) => ({
      images: state.images.map((item) => ({ ...item, status: 'pending', result: null, error: '' })),
      highlightedDetection: null,
    })),

  reset: () => {
    get().images.forEach(revoke);
    set({
      catalogue: null,
      catalogueLoading: false,
      catalogueError: '',
      search: '',
      selectedVersionId: null,
      conf: 0.25,
      images: EMPTY,
      activeImageId: null,
      rejected: EMPTY,
      running: false,
      highlightedDetection: null,
    });
  },
}));

export default usePredictionStore;
